import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";
import { sitePricingQuery } from "@/lib/pricing-query";

import { PageHero } from "../../components/site/Page";
import { EnrollmentForm } from "../../components/site/EnrollmentForm";
import { StepRow } from "../../components/site/StepRow";

const title = "Application to School and Preschool | SIS Estepona";
const description =
  "Apply for your child to Swedish International School in Estepona/Marbella. Applications are accepted on a rolling basis and it's fine to start mid-term.";

export const Route = createFileRoute("/en/application")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/application") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/application"),
  }),
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(sitePricingQuery);
  },
  component: Application,
});

const steps = ["Fill in the form", "Visit and conversation", "Place confirmation"];

function Application() {
  return (
    <>
      <PageHero
        eyebrow="Application"
        title="Application to school and preschool"
        intro="Applications are open to pupils of all nationalities aged 3 to 18. Applications are accepted on a rolling basis and it's fine to start mid-term if needed."
      />

      <StepRow steps={steps} />

      <section id="formular" className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-semibold">Application form</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Fill in the details below and we'll calculate the fee straight away based on school form,
          period and any add-ons. The application is not binding – the school will get back to you
          with a decision on a place.
        </p>
        <div className="mt-10">
          <EnrollmentForm locale="en" />
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 pb-16">
        <div className="rounded-xl bg-sand p-8">
          <h2 className="text-xl font-semibold">Contact the principal to apply</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            If you have any questions about your application, you are always welcome to contact the
            school's principal.
          </p>
          <div className="mt-5 flex flex-wrap gap-4">
            <a
              href="mailto:rektor@sweintschool.com"
              className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              rektor@sweintschool.com
            </a>
            <a
              href="tel:+34607148331"
              className="rounded-md border border-border bg-background px-5 py-2.5 text-sm font-medium transition-colors hover:border-primary"
            >
              +34 607 148 331
            </a>
          </div>
        </div>

        <p className="mt-8 text-sm text-muted-foreground">
          More answers about levels, short-term enrolment, several children and the agreement are on our{" "}
          <a className="text-primary link-quiet" href="/en/questions-and-answers">questions and answers</a> page.
        </p>
      </section>
      <FaqBlock items={faqFor("anmalan", "en")} locale="en" />
    </>
  );
}
